import { useState } from "react";
import { Box, CircularProgress, ClickAwayListener, Divider, Grow, MenuItem, MenuList, Paper, Popper, Typography } from "@mui/material";
import { QueuedReport } from "../../../types/models/QueuedReport";
import { AppColor } from "../../../types/Constants";
import { useAppDispatch } from "../../../utils/hooks";
import { BanUser, RemoveBeacon, dismissReport } from "../../../redux/actions";
import AppConfirmDialog from "../../../components/AppConfirmDialog";

type QueuedReportListMenuProps = {
  open: boolean;
  anchorElement: HTMLButtonElement | null;
  setIsOpen: (open: boolean) => void;
  openDetail: () => void;
  report: QueuedReport;
  setRefreshFlag: (refresh: boolean) => void;
};

const QueuedReportListMenu = ({ open, anchorElement, setIsOpen, openDetail, report, setRefreshFlag }: QueuedReportListMenuProps) => {

  const QueuedReportActions = ["Dismiss", "Delete Beacon", "Ban User"] as const;
  const dispatch = useAppDispatch();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [confirmDialogAction, setConfirmDialogAction] = useState<(typeof QueuedReportActions)[number]>("Dismiss");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const getConfirmMessage = () => {
    switch (confirmDialogAction) {
      case "Dismiss":
        return <Typography>Are you sure you want to <span style={{ color: "red" }}>dismiss</span> the report for the {report.report_type.toLowerCase()} &quot;{report.report_title}&quot;?</Typography>;
      case "Delete Beacon":
        return <Typography>Are you sure you want to <span style={{ color: "red" }}>delete</span> the beacon &quot;{report.report_title}&quot;?</Typography>;
      case "Ban User":
        return <Typography>Are you sure you want to <span style={{ color: "red" }}>ban</span> the user &quot;{report.report_title}&quot;?</Typography>;
      default:
        return "";
    }
  };

  const handleOnConfirm = () => {
    setIsOpen(false);
    setConfirmDialogOpen(false);
    setIsSubmitting(true);
    switch (confirmDialogAction) {
      case "Dismiss":
        dispatch(dismissReport(
          report.id,
          (result) => {
            if (result) { setRefreshFlag(true); }
            setIsSubmitting(false);
          }));
        break;
      case "Delete Beacon":
        dispatch(RemoveBeacon(
          report.report_id ?? "",
          (result) => {
            if (result) { setRefreshFlag(true); }
            setIsSubmitting(false);
          },
          report.id));
        break;
      case "Ban User":
        dispatch(BanUser(
          report.reported_user_id ?? "",
          (result) => {
            if (result) { setRefreshFlag(true); }
            setIsSubmitting(false);
          },
          report.id));
        break;
    }
  };

  return (
    <>
      <Popper
        open={open}
        anchorEl={anchorElement}
        role={undefined}
        placement="bottom-start"
        transition
      >
        {/* Menu Entities */}
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom-start" ? "left top" : "left bottom",
            }}
          >
            <Paper sx={{ backgroundColor: "white", zIndex: 9999 }}>
              <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                <MenuList
                  id="composition-menu"
                  aria-labelledby="composition-button"
                >
                  <MenuItem
                    onClick={() => {
                      setIsOpen(false);
                      openDetail();
                    }}
                  >
                    View
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setIsOpen(false);
                      setConfirmDialogAction("Dismiss");
                      setConfirmDialogOpen(true);
                    }}
                    sx={{ color: "red" }}
                  >
                    Dismiss
                  </MenuItem>
                  <Divider />
                  {report.report_type === "Beacon" &&
                    <MenuItem
                      onClick={() => {
                        setIsOpen(false);
                        setConfirmDialogAction("Delete Beacon");
                        setConfirmDialogOpen(true);
                      }}
                      sx={{ color: "red" }}
                    >
                      Delete Beacon
                    </MenuItem>
                  }
                  <MenuItem
                    onClick={() => {
                      setIsOpen(false);
                      setConfirmDialogAction("Ban User");
                      setConfirmDialogOpen(true);
                    }}
                    sx={{ color: "red" }}
                  >
                    Ban User
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      <AppConfirmDialog
        open={confirmDialogOpen}
        onConfirm={() => {
          setIsOpen(false);
          handleOnConfirm();
        }}
        onDeny={() => {
          setIsOpen(false);
          setConfirmDialogOpen(false);
        }}
        title={getConfirmMessage()}
        dialogTitle={confirmDialogAction}
        content=""
      />
      {isSubmitting && <Box sx={{
        position: "absolute",
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}>
        <CircularProgress sx={{ color: AppColor.baseOrange }} />
      </Box>}
    </>
  );
};

export default QueuedReportListMenu;
